// SC: Provides reload-free switching between the native cart drawer and UpCart.
(() => {
  const scStorageKey = 'sc_cart_variant';
  const scSupportedVariants = ['native', 'upcart'];
  const scCompareMode = new URLSearchParams(window.location.search).get('sc_cart_compare') === '1';
  const scEarlyQueue = window.__scCartExperimentQueue || [];
  let scOperationId = 0;
  let scNativeSyncCount = 0;
  let scNativeSyncCooldownUntil = 0;
  let scMutationTimer = null;
  let scPendingMutation = null;

  const scGetVariant = () => (window.scCartVariant === 'native' ? 'native' : 'upcart');
  const scIsNative = () => scGetVariant() === 'native';
  const scGetNativeDrawer = () => document.querySelector('sc-cart-drawer');
  const scStartOperation = () => ++scOperationId;
  const scIsOperationCurrent = (operationId, expectedVariant) =>
    operationId === scOperationId && scGetVariant() === expectedVariant;
  const scIsNativeSyncing = () =>
    scNativeSyncCount > 0 ||
    scGetNativeDrawer()?.hasAttribute('data-sc-cart-gift-syncing') ||
    Date.now() < scNativeSyncCooldownUntil;
  const scGetNativeIdleDelay = () =>
    scNativeSyncCount > 0
      ? 100
      : Math.max(50, scNativeSyncCooldownUntil - Date.now() + 20);
  const scWaitForNativeIdle = (timeout = 4000) =>
    new Promise((resolve) => {
      const startedAt = Date.now();

      const check = () => {
        if (!scIsNativeSyncing() || Date.now() - startedAt >= timeout) {
          resolve();
          return;
        }

        window.setTimeout(check, scGetNativeIdleDelay());
      };

      check();
    });

  const scSetUpcartReadyState = (ready) => {
    document.documentElement.dataset.scUpcartReady = ready ? 'true' : 'false';
    return ready;
  };

  const scIsUpcartReady = () =>
    scSetUpcartReadyState(
      typeof window.upcartRefreshCart === 'function' &&
        typeof window.upcartOpenCart === 'function'
    );

  const scPersistVariant = (variant) => {
    try {
      window.sessionStorage.setItem(scStorageKey, variant);
    } catch (error) {
      // SC: The external A/B script can still control the current page in memory.
    }
  };

  const scSyncUpcartBodyFlag = () => {
    if (!document.body) return;

    if (scCompareMode) {
      if (
        window.__upcartInitialized &&
        document.body.getAttribute('data-upcart-cart-enabled') !== 'true'
      ) {
        document.body.setAttribute('data-upcart-cart-enabled', 'true');
      }
    } else if (scIsNative()) {
      document.body.removeAttribute('data-upcart-cart-enabled');
    } else if (
      window.__upcartInitialized &&
      document.body.getAttribute('data-upcart-cart-enabled') !== 'true'
    ) {
      document.body.setAttribute('data-upcart-cart-enabled', 'true');
    }
  };

  const scCloseUpcart = () => {
    try {
      window.upcartCloseCart?.();
    } catch (error) {
      console.warn('[SC cart experiment] UpCart could not be closed.', error);
    }

    document.body?.classList.remove('upcartPopupShow', 'upcartPopupClosing');
  };

  const scCloseNative = () => {
    scGetNativeDrawer()?.close?.();
    document.body?.classList.remove('sc-cart-drawer-open');
  };

  // SC: Keep scroll locks and open-state classes owned by the selected cart only.
  const scEnforceSelectedCartBodyState = () => {
    scSyncUpcartBodyFlag();

    if (scCompareMode) return;

    if (scIsNative()) {
      if (
        document.body?.classList.contains('upcartPopupShow') ||
        document.body?.classList.contains('upcartPopupClosing')
      ) {
        scCloseUpcart();
      }
      return;
    }

    if (
      document.body?.classList.contains('sc-cart-drawer-open') ||
      scGetNativeDrawer()?.classList.contains('active')
    ) {
      scCloseNative();
    }
  };

  const scApplyVariant = () => {
    const native = scIsNative();

    document.documentElement.dataset.scCartVariant = scGetVariant();
    scIsUpcartReady();
    window.upcartOpenOnAddToCartOverride = scCompareMode || !native;

    if (window.upcartSettings?.upcartSettings) {
      window.upcartSettings.upcartSettings.openOnAddToCart = scCompareMode || !native;
    }

    if (window.upcartSettings?.upcartEditorSettings) {
      window.upcartSettings.upcartEditorSettings.cartIsEnabled = true;
    }

    if (!scCompareMode) {
      if (native) {
        scCloseUpcart();
      } else {
        scCloseNative();
      }
    }

    scSyncUpcartBodyFlag();
  };

  const scWaitFor = (getValue, label, { operationId, expectedVariant, timeout = 10000 } = {}) =>
    new Promise((resolve, reject) => {
      const startedAt = Date.now();
      let retryDelay = 50;

      const scCheck = () => {
        if (
          operationId !== undefined &&
          !scIsOperationCurrent(operationId, expectedVariant)
        ) {
          resolve(null);
          return;
        }

        const value = getValue();

        if (value) {
          resolve(value);
          return;
        }

        if (Date.now() - startedAt >= timeout) {
          reject(new Error(`[SC cart experiment] ${label} is unavailable`));
          return;
        }

        window.setTimeout(scCheck, retryDelay);
        retryDelay = Math.min(Math.ceil(retryDelay * 1.5), 500);
      };

      scCheck();
    });

  const scWaitForNativeDrawer = (operation) =>
    scWaitFor(
      () => {
        const drawer = scGetNativeDrawer();
        return drawer &&
          typeof drawer.getSectionsToRender === 'function' &&
          typeof drawer.updateRenderedSections === 'function'
          ? drawer
          : null;
      },
      'sc-cart-drawer',
      operation
    );

  const scOpenNative = async (activeElement) => {
    const currentDrawer = scGetNativeDrawer();
    const drawer =
      currentDrawer && typeof currentDrawer.open === 'function'
        ? currentDrawer
        : await scWaitForNativeDrawer();

    if (!drawer || !scIsNative()) return null;

    drawer.open(activeElement);
    return drawer;
  };

  const scNativeDrawerMatchesCart = (cart) => {
    if (!Array.isArray(cart?.items)) return false;

    const drawer = scGetNativeDrawer();
    const renderedItems = Array.from(
      drawer?.querySelectorAll('[data-sc-cart-line][data-sc-cart-key]') || []
    );

    if (renderedItems.length !== cart.items.length) return false;

    const cartItemCount = Number.isFinite(Number(cart.item_count))
      ? Number(cart.item_count)
      : cart.items.reduce((total, item) => total + Number(item.quantity || 0), 0);
    const renderedItemCount = Number(drawer?.dataset.scCartItemCount);

    if (Number.isFinite(renderedItemCount) && renderedItemCount !== cartItemCount) return false;

    const cartTotalPrice = Number(cart.total_price ?? cart.total);
    const renderedTotalPrice = Number(drawer?.dataset.scCartTotalPrice);

    if (
      Number.isFinite(cartTotalPrice) &&
      Number.isFinite(renderedTotalPrice) &&
      renderedTotalPrice !== cartTotalPrice
    ) {
      return false;
    }

    const renderedQuantities = new Map(
      renderedItems.map((item) => {
        const quantityInput = item.querySelector('[data-sc-cart-quantity-input]');
        const quantity = quantityInput?.value ?? item.dataset.scCartQuantity;

        return [item.dataset.scCartKey, Number(quantity || 0)];
      })
    );

    return cart.items.every(
      (item) => renderedQuantities.get(item.key) === Number(item.quantity)
    );
  };

  const scFetchCartSnapshot = async () => {
    const cartUrl = window.routes?.cart_url || '/cart';
    const snapshotUrl = `${cartUrl.replace(/\/$/, '')}.js`;
    const response = await fetch(snapshotUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`[SC cart experiment] Cart verification failed: ${response.status}`);
    }

    return response.json();
  };

  const scRefreshNativeInternal = async ({
    open = false,
    activeElement,
    operationId,
    expectedVariant,
  }) => {
    const operation = { operationId, expectedVariant };
    const drawer = await scWaitForNativeDrawer(operation);

    if (!drawer || !scIsOperationCurrent(operationId, expectedVariant)) return null;

    if (activeElement) drawer.setActiveElement(activeElement);

    // SC: Opening is a UI action and must never wait for section synchronization.
    if (open && scIsNative()) drawer.open(activeElement);

    scNativeSyncCount += 1;

    try {
      const sections = drawer.getSectionsToRender().map((section) => section.id);
      const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          updates: {},
          sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error(`[SC cart experiment] Native cart refresh failed: ${response.status}`);
      }

      const cart = await response.json();

      if (!scIsOperationCurrent(operationId, expectedVariant)) return cart;

      drawer.updateRenderedSections(cart.sections || {}, { source: 'sc-cart-experiment' });

      return cart;
    } finally {
      scNativeSyncCount = Math.max(0, scNativeSyncCount - 1);

      if (scNativeSyncCount === 0) {
        scNativeSyncCooldownUntil = Date.now() + 600;
      }
    }
  };

  const scRefreshUpcartInternal = async ({ open = false, operationId, expectedVariant }) => {
    const operation = { operationId, expectedVariant, timeout: 4000 };
    const refresh = await scWaitFor(
      () => (scIsUpcartReady() ? window.upcartRefreshCart : null),
      'upcartRefreshCart',
      operation
    );

    if (!refresh || !scIsOperationCurrent(operationId, expectedVariant)) return null;

    // SC: Start the drawer transition immediately; the cart refresh can complete
    // while the panel is already moving into view.
    if (open) {
      const openCart = window.upcartOpenCart;

      if (typeof openCart !== 'function') {
        throw new Error('[SC cart experiment] upcartOpenCart is unavailable');
      }

      await openCart();

      if (!scIsOperationCurrent(operationId, expectedVariant)) {
        scCloseUpcart();
        return null;
      }
    }

    const cart = await refresh();

    if (!scIsOperationCurrent(operationId, expectedVariant)) {
      if (open) scCloseUpcart();
      return cart;
    }

    return cart;
  };

  const scActivateVariant = ({ variant, open = false, activeElement, operationId }) => {
    if (variant === 'native') {
      return scRefreshNativeInternal({
        open,
        activeElement,
        operationId,
        expectedVariant: variant,
      });
    }

    return scRefreshUpcartInternal({
      open,
      operationId,
      expectedVariant: variant,
    });
  };

  const scSetVariant = (variant, { open = false, activeElement } = {}) => {
    if (!scSupportedVariants.includes(variant)) {
      throw new TypeError('[SC cart experiment] Variant must be "native" or "upcart"');
    }

    const variantChanged = scGetVariant() !== variant;
    const operationId = scStartOperation();

    window.scCartVariant = variant;
    scPersistVariant(variant);
    scApplyVariant();

    if (variantChanged) {
      document.dispatchEvent(new CustomEvent('sc:cart-variant-change', { detail: { variant } }));
    }

    return scActivateVariant({ variant, open, activeElement, operationId })
      .then(() => {
        if (scIsOperationCurrent(operationId, variant)) {
          document.dispatchEvent(new CustomEvent('sc:cart-variant-ready', { detail: { variant } }));
        }

        return variant;
      })
      .catch((error) => {
        if (scIsOperationCurrent(operationId, variant)) {
          console.warn(`[SC cart experiment] ${variant} cart could not be synchronized.`, error);

          document.dispatchEvent(
            new CustomEvent('sc:cart-variant-error', { detail: { variant, error } })
          );

          if (variant === 'upcart') {
            return scSetVariant('native', { open, activeElement });
          }

          if (open) scGetNativeDrawer()?.open?.(activeElement);
        }

        return variant;
      });
  };

  const scOpen = async ({ activeElement } = {}) => {
    const variant = scGetVariant();

    scApplyVariant();

    if (variant === 'native') {
      try {
        const drawer = await scOpenNative(activeElement);

        if (drawer) return drawer;
      } catch (error) {
        if (scIsUpcartReady()) {
          console.warn('[SC cart experiment] Native cart is unavailable; opening UpCart.', error);
          return scSetVariant('upcart', { open: true, activeElement });
        }

        console.error(error);
      }

      window.location.assign(window.routes?.cart_url || '/cart');
      return null;
    }

    if (!scIsUpcartReady()) {
      console.warn('[SC cart experiment] UpCart is unavailable; opening sc-cart-drawer.');
      return scSetVariant('native', { open: true, activeElement });
    }

    const operationId = scStartOperation();

    try {
      return await scActivateVariant({ variant, open: true, activeElement, operationId });
    } catch (error) {
      if (!scIsOperationCurrent(operationId, variant)) return null;

      if (variant === 'upcart') {
        console.warn('[SC cart experiment] UpCart is unavailable; opening sc-cart-drawer.', error);
        return scSetVariant('native', { open: true, activeElement });
      }

      return null;
    }
  };

  const scRefreshNative = ({ open = false, activeElement } = {}) => {
    const expectedVariant = scGetVariant();
    const operationId = scStartOperation();

    return scRefreshNativeInternal({
      open,
      activeElement,
      operationId,
      expectedVariant,
    });
  };

  const scFlushCartMutation = async () => {
    const mutation = scPendingMutation;

    scPendingMutation = null;
    scMutationTimer = null;

    if (!mutation) return null;

    if (window.scCartIntegrationsDisabled === true || !scIsNative()) {
      mutation.resolve(null);
      return null;
    }

    if (mutation.waitForNativeIdle && scIsNativeSyncing()) {
      scPendingMutation = mutation;
      scMutationTimer = window.setTimeout(scFlushCartMutation, scGetNativeIdleDelay());
      return null;
    }

    if (mutation.verifyBeforeRefresh) {
      try {
        let cartSnapshot = await scFetchCartSnapshot();

        if (scNativeDrawerMatchesCart(cartSnapshot)) {
          mutation.resolve(cartSnapshot);
          return cartSnapshot;
        }

        if (scIsNativeSyncing()) {
          await scWaitForNativeIdle();
          cartSnapshot = await scFetchCartSnapshot();

          if (scNativeDrawerMatchesCart(cartSnapshot)) {
            mutation.resolve(cartSnapshot);
            return cartSnapshot;
          }
        }
      } catch (error) {
        // SC: Verification is an optimization. A section refresh remains the safe fallback.
      }
    }

    const sources = Array.from(mutation.sources);
    const hasOnlyUpcartNotifications =
      sources.length > 0 &&
      sources.every((source) => source === 'gbbExt-shopify-cart-changed');
    const upcartCart = hasOnlyUpcartNotifications ? window.upcartGetCart?.() : null;

    if (upcartCart && scNativeDrawerMatchesCart(upcartCart)) {
      mutation.resolve(upcartCart);
      return upcartCart;
    }

    try {
      const cart = await scRefreshNative({
        activeElement: mutation.activeElement,
      });

      mutation.resolve(cart);
      return cart;
    } catch (error) {
      console.warn('[SC cart experiment] External cart mutation could not be synchronized.', error);
      mutation.resolve(null);
      return null;
    }
  };

  const scHandleCartMutation = ({
    source = 'external',
    open = false,
    activeElement,
    ignoreDuringSync = false,
    delay = 160,
  } = {}) => {
    if (window.scCartIntegrationsDisabled === true || !scIsNative()) {
      return Promise.resolve(null);
    }

    const waitForNativeIdle = ignoreDuringSync && scIsNativeSyncing();
    const verifyBeforeRefresh = ignoreDuringSync;

    if (open) {
      scOpenNative(activeElement).catch((error) => {
        console.warn('[SC cart experiment] Native cart could not be opened.', error);
      });
    }

    return new Promise((resolve) => {
      if (scPendingMutation) {
        const previousResolve = scPendingMutation.resolve;

        scPendingMutation.activeElement = activeElement || scPendingMutation.activeElement;
        scPendingMutation.sources.add(source);
        scPendingMutation.waitForNativeIdle =
          scPendingMutation.waitForNativeIdle || waitForNativeIdle;
        scPendingMutation.verifyBeforeRefresh =
          scPendingMutation.verifyBeforeRefresh || verifyBeforeRefresh;
        scPendingMutation.resolve = (value) => {
          previousResolve(value);
          resolve(value);
        };
      } else {
        scPendingMutation = {
          activeElement,
          sources: new Set([source]),
          waitForNativeIdle,
          verifyBeforeRefresh,
          resolve,
        };
      }

      window.clearTimeout(scMutationTimer);
      scMutationTimer = window.setTimeout(scFlushCartMutation, Math.max(0, delay));
    });
  };

  const scAfterAdd = (cartResponse, { nativeCart } = {}) => {
    const variant = scGetVariant();
    const operationId = scStartOperation();

    scApplyVariant();

    const updateSelectedCart = async () => {
      if (variant === 'upcart') {
        return scRefreshUpcartInternal({ open: true, operationId, expectedVariant: variant });
      }

      const drawer =
        nativeCart && typeof nativeCart.updateRenderedSections === 'function'
          ? nativeCart
          : await scWaitForNativeDrawer({ operationId, expectedVariant: variant });

      if (!drawer || !scIsOperationCurrent(operationId, variant)) return null;

      if (cartResponse?.sections) {
        drawer.updateRenderedSections(cartResponse.sections, { source: 'sc-after-add' });
        drawer.open();
        return cartResponse;
      }

      drawer.open();

      return scRefreshNativeInternal({
        operationId,
        expectedVariant: variant,
      });
    };

    return updateSelectedCart().catch((error) => {
      if (!scIsOperationCurrent(operationId, variant)) return null;

      if (variant === 'upcart') {
        console.warn('[SC cart experiment] UpCart could not handle add to cart; using sc-cart-drawer.', error);
        return scSetVariant('native', { open: true });
      }

      console.error(error);
      scGetNativeDrawer()?.open?.();
      return null;
    });
  };

  scApplyVariant();

  if (document.body) {
    new MutationObserver(scEnforceSelectedCartBodyState).observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'data-upcart-cart-enabled'],
    });
  }

  const scApi = window.scCartExperiment || {};

  Object.assign(scApi, {
    scAfterAdd,
    scGetVariant,
    scHandleCartMutation,
    scIsNative,
    scIsNativeSyncing,
    scOpen,
    scRefreshNative,
    scSetVariant,
  });

  window.scCartExperiment = scApi;

  const queuedCalls = scEarlyQueue.splice(0);

  queuedCalls.forEach(({ method, args, resolve, reject }) => {
    Promise.resolve()
      .then(() => scApi[method](...args))
      .then(resolve, reject);
  });

  if (queuedCalls.length === 0) {
    if (scIsNative()) {
      document.dispatchEvent(
        new CustomEvent('sc:cart-variant-ready', { detail: { variant: 'native' } })
      );
    } else {
      scSetVariant(scGetVariant());
    }
  }

  document.dispatchEvent(new CustomEvent('sc:cart-experiment-ready'));
})();
